import React, { useEffect, useState } from "react";
import { useDraggableSheet } from "../../../helpers/hooks";
import Icon from "../Icon/Icon";
import './EditMediaPreview.scss'
import { useDispatch, useSelector } from "react-redux";
import { fetchHeatmapTags } from "../../Reducers/actions/heatmapActions";
import CustomSelect from "../CustomSelect/CustomSelect";
import { supabase } from "../../../supabase";
import { updateCreation } from "../../Reducers/actions/creationsActions";
import { cachingService } from "../../../helpers/cache";

const EditMediaPreview = ({ creation_id, onClose }) => {
    const { user_id } = useSelector(state => state.auth.user?.user_id)
    const tags = useSelector(state => state.heatmap?.tags)
    const creation = useSelector(state => state.creation?.creation)

    const [category, setCategory] = useState('');
    const [loading, setLoading] = useState(false)
    const [mediaFile, setMediaFile] = useState(null);
    const [mediaPreview, setMediaPreview] = useState('');
    const [previewFile, setPreviewFile] = useState(null);
    const [previewImage, setPreviewImage] = useState('');

    const dispatch = useDispatch()

    useEffect(() => {
        if (!(tags && tags.length > 0)) {
            dispatch(fetchHeatmapTags())
        }
    }, [dispatch, tags]);


    const {
        sheetStyle,
        overlayHandlers,
    } = useDraggableSheet({ onClose });


    const handleSubmit = async () => {
        setLoading(true)
        const fields = { creation_id, category }
        
        // Upload with timeout wrapper
        const uploadWithTimeout = async (uploadPromise, timeoutMs = 120000) => {
            return Promise.race([
                uploadPromise,
                new Promise((_, reject) =>
                    setTimeout(() => reject(new Error('Upload timeout - please check your connection and try again')), timeoutMs)
                )
            ]);
        };

        try {
            let fileExt = mediaFile.name.split('.').pop();
            let fileName = `${user_id}_${Date.now()}.${fileExt}`;
            let filePath = `creations-media/${user_id}/${fileName}`;

            // Use resumable upload for files > 6MB
            const useResumable = mediaFile.size > 6 * 1024 * 1024;
            const timeout = useResumable ? 180000 : 60000; // 3 min for large, 1 min for small

            const uploadResult = await uploadWithTimeout(
                supabase.storage
                    .from("creations-files")
                    .upload(filePath, mediaFile, { 
                        contentType: mediaFile.type,
                        cacheControl: '3600',
                        upsert: false,
                    }),
                timeout
            );

            if (uploadResult.error) {
                console.error('Upload error:', uploadResult.error);
                alert(`Upload failed: ${uploadResult.error.message || 'Unknown error'}. Please try again.`);
                setLoading(false);
                return;
            }

            fields.media_url = supabase.storage
                .from('creations-files')
                .getPublicUrl(filePath).data.publicUrl;

            // Upload thumbnail with timeout
            if (previewFile) {
                fileExt = previewFile.name.split('.').pop();
                fileName = `${user_id}_${Date.now()}.${fileExt}`;
                filePath = `creations-thumbnail/${user_id}/${fileName}`;

                const thumbnailResult = await uploadWithTimeout(
                    supabase.storage
                        .from("creations-files")
                        .upload(filePath, previewFile, { 
                            contentType: previewFile.type,
                            cacheControl: '3600',
                            upsert: false,
                        }),
                    60000 // 1 minute timeout for thumbnails
                );

                if (thumbnailResult.error) {
                    console.error('Thumbnail upload error:', thumbnailResult.error);
                    alert(`Thumbnail upload failed: ${thumbnailResult.error.message}. Please try again.`);
                    setLoading(false);
                    return;
                }

                const newThumbnailUrl = supabase.storage
                    .from('creations-files')
                    .getPublicUrl(filePath).data.publicUrl;
                
                fields.thumbnail_url = newThumbnailUrl;
                
                // Optimistic update: Update UI immediately with new thumbnail
                const previousThumbnailUrl = creation?.thumbnail_url || null;
                dispatch({
                    type: 'OPTIMISTIC_UPDATE_CREATION_THUMBNAIL',
                    payload: {
                        creation_id: creation_id,
                        thumbnail_url: newThumbnailUrl,
                        previous_thumbnail_url: previousThumbnailUrl
                    }
                });
            }
        } catch (error) {
            console.error('Upload error:', error);
            alert(error.message || 'Upload failed. Please check your connection and try again.');
            setLoading(false);
            return;
        }

        updateCreation(creation_id, fields).then(({ success, data }) => {
            if (success) {
                // Invalidate both user's creations list and the specific creation detail cache
                cachingService.invalidate([
                    `/api/creations/${user_id}`,
                    `/api/creations/detail/${creation_id}`
                ], true);
                dispatch({
                    type: 'UPDATE_CREATION_SUCCESS', payload: {
                        creation_id: creation_id,
                        media_url: fields.media_url,
                        thumbnail_url: fields.thumbnail_url,
                        category: fields.category,
                        media: {
                            ...data.media,
                        }
                    }
                })
                onClose()
            } else {
                // Rollback optimistic update if API call failed
                if (previewFile && fields.thumbnail_url) {
                    dispatch({
                        type: 'ROLLBACK_CREATION_THUMBNAIL',
                        payload: {
                            creation_id: creation_id
                        }
                    });
                }
                setLoading(false)
                alert('An error occurred while publishing your creation. Please try again.');
            }
        }).catch((error) => {
            console.error('Error updating creation:', error);
            // Rollback optimistic update on error
            if (previewFile && fields.thumbnail_url) {
                dispatch({
                    type: 'ROLLBACK_CREATION_THUMBNAIL',
                    payload: {
                        creation_id: creation_id
                    }
                });
            }
            setLoading(false);
            alert('An error occurred while publishing your creation. Please try again.');
        })
    }

    const handleFileChange = async (e, type) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.size > 1024 * 1024 * 1024) {
            alert('File size must be less than 1GB');
            return;
        }

        const previewUrl = URL.createObjectURL(file);

        if (type === 'media') {
            setMediaFile(file);
            setMediaPreview(previewUrl);
        } else if (type === 'thumbnail') {
            setPreviewFile(file);
            setPreviewImage(previewUrl);
        }
    };

    const disabled = loading || !mediaFile || !mediaPreview | !previewFile || !previewImage || !category.length

    return (
        <div className="edit-media-container" {...overlayHandlers}>
            <div className="sheet" style={sheetStyle} onClick={(e) => e.stopPropagation()}>
                <div className="d-flex aic jcc edit-media-header mb-8">
                    <Icon alias={"chevron-left"} width={24} color="#0A0F1E" className={"p-absolute"} style={{ left: 16 }} onClick={onClose} />
                    <span className="fg-black fw-bold fs-sm2">Media</span>
                    <button
                        className="btn btn-outline-white-v2 btn-sm ml-16"
                        style={{ minWidth: 80, fontWeight: 600, opacity: disabled ? 0.5 : 1, top: '24px', right: '16px', zIndex: 1000, position: 'absolute' }}
                        disabled={disabled}
                        onClick={handleSubmit}
                    >
                        {loading ? "Saving..." : "Save"}
                    </button>
                </div>
                <div className="content">
                    <CustomSelect
                        value={category}
                        onChange={setCategory}
                        className="select mb-24"
                        name="category"
                        options={[
                            { value: '', label: 'Are you sharing a music, photograph or other?' },
                            {
                                value: 'music',
                                label: (
                                    <>
                                        <Icon alias="music" className={'mr-8'} width={32} height={32} color="#FF3232" />
                                        <span>Music</span>
                                    </>
                                )
                            },
                            {
                                value: 'podcast',
                                label: (
                                    <>
                                        <Icon alias="podcast" className={'mr-8'} width={32} height={32} color="#FF3232" />
                                        <span>Podcast</span>
                                    </>
                                )
                            },
                            {
                                value: 'video',
                                label: (
                                    <>
                                        <Icon alias="video" className={'mr-8'} width={32} height={32} color="#FF3232" />
                                        <span>Video/Movie</span>
                                    </>
                                )
                            },
                            {
                                value: 'artwork',
                                label: (
                                    <>
                                        <Icon alias="artwork" className={'mr-8'} width={32} height={32} color="#FF3232" />
                                        <span>Artwork</span>
                                    </>
                                )
                            },
                            {
                                value: 'photography',
                                label: (
                                    <>
                                        <Icon alias="photography" className={'mr-8'} width={32} height={32} color="#FF3232" />
                                        <span>Photography</span>
                                    </>
                                )
                            },
                            {
                                value: 'clothing',
                                label: <>
                                    <Icon alias="clothing" className={'mr-8'} width={32} height={32} color="#FF3232" />
                                    <span>Clothing</span>
                                </>
                            },
                            {
                                value: 'book',
                                label: <>
                                    <Icon alias="ebook" className={'mr-8'} width={32} height={32} color="#FF3232" />
                                    <span>Book/E-Book</span>
                                </>
                            },
                            {
                                value: 'craft',
                                label: <>
                                    <Icon alias="craft" className={'mr-8'} width={32} height={32} color="#FF3232" />
                                    <span>Handmade Craft</span>
                                </>
                            },
                        ]}
                    />
                    {mediaFile && <span
                        className='main-preview-label mb-24'
                        onClick={() => {
                            setMediaFile(null);
                            setMediaPreview('');
                            setPreviewFile(null);
                            setPreviewImage('');
                        }}
                    >{mediaFile.name}<Icon alias={'trash'} width={24} color='transparent' stroke='#C9100E' className='ml-auto' /></span>}

                    {previewImage && <span
                        className='main-preview-label mb-24'
                        onClick={() => {
                            setPreviewFile(null);
                            setPreviewImage('');
                        }}
                    >{previewFile.name}<Icon alias={'trash'} width={24} color='transparent' stroke='#C9100E' className='ml-auto' /></span>}


                    {!mediaPreview && <div className="upload-area">
                        <label className="upload-button">
                            <input
                                type="file"
                                onChange={(e) => handleFileChange(e, 'media')}
                                accept=".png,.jpg,.jpeg,.gif,.mp4,.webm,.ogg,.mp3,.wav,.flac,.pdf"
                                hidden
                            />
                            <div className="upload-placeholder">
                                <i className="fas fa-plus"></i>
                                <span>Add your creation</span>
                                <p>Images (PNG, JPG, GIF), Videos (MP4, WebM, OGG), Audio (MP3, WAV, FLAC, OGG), PDF up to 1GB</p>
                            </div>
                        </label>
                    </div>}

                    {mediaPreview && !previewImage &&
                        <div className="preview-upload">
                            <label className="upload-button">
                                <input
                                    type="file"
                                    onChange={(e) => handleFileChange(e, 'thumbnail')}
                                    accept="image/*"
                                    hidden
                                />
                                <div className="upload-placeholder">
                                    <i className="fas fa-plus"></i>
                                    <span>Add a thumbnail</span>
                                </div>
                            </label>
                        </div>
                    }
                </div>

            </div>
        </div>
    )
}

export default EditMediaPreview